<script setup lang="ts">
import type { InputProps } from '.';

import { cn } from '@/utils';
import { useVModel } from '@vueuse/core';

import { inputContainerVariants, inputVariants } from '.';

// Prevent disabled from being passed via $attrs (we handle it explicitly via props)
defineOptions({
	inheritAttrs: false
});

// Filter out 'disabled' from $attrs since we handle it via props
const attrs = useAttrs();
const filteredAttrs = computed(() => {
	const { disabled: _disabled, ...rest } = attrs;
	return rest;
});

const props = withDefaults(defineProps<InputProps>(), {
	variant: 'default',
	unitPosition: 'right',
	autofocus: false,
	cyTestId: '',
	disabled: false
});

const emits = defineEmits<{
	(e: 'update:modelValue', payload: string | number): void;
	(e: 'update:isFocused', payload: boolean): void;
}>();

const modelValue = useVModel(props, 'modelValue', emits, {
	passive: true,
	defaultValue: props.defaultValue
});

const highlightedPlaceholder = computed(() => {
	if (props.variant !== 'default' || !props.highlightRequired) return null;
	return props.placeholder?.match(/^(.*)\*\s*$/)?.[1] ?? null;
});

const inputRef = useTemplateRef<HTMLInputElement>('inputRef');

const { focused } = useFocus(inputRef);

watch(focused, newValue => {
	emits('update:isFocused', newValue);
});

whenever(
	() => props.autofocus,
	async () => {
		if (!props.autofocus) return;

		await nextTick();

		if (!inputRef.value) return;

		inputRef.value.focus();
	},
	{ immediate: true }
);

defineExpose({
	focused,
	focus: () => inputRef.value?.focus()
});
</script>

<template>
	<div
		:class="
			cn(inputContainerVariants({ variant }), 'base-input', {
				relative: highlightedPlaceholder !== null
			})
		"
	>
		<input
			ref="inputRef"
			v-model="modelValue"
			:data-test-id="cyTestId"
			:class="[
				cn(
					inputVariants({ variant: props.variant }),
					props.class,
					highlightedPlaceholder !== null && 'peer placeholder:text-transparent'
				),
				{ 'border-error!': props.error }
			]"
			:disabled="props.disabled"
			v-bind="filteredAttrs"
			:placeholder="props.variant === 'insideLabel' ? ' ' : props.placeholder"
		/>
		<span
			v-if="highlightedPlaceholder !== null"
			aria-hidden="true"
			class="pointer-events-none absolute inset-y-0 left-4 hidden items-center whitespace-pre text-sm text-primary-light-1 peer-placeholder-shown:flex"
		>
			<span>{{ highlightedPlaceholder }}</span
			><span class="text-tertiary">*</span>
		</span>
		<slot name="affix" />
		<label
			v-if="props.variant === 'insideLabel'"
			:for="filteredAttrs.id as string"
			:class="[
				cn(
					'absolute left-4 top-[25%] z-20 cursor-text select-none text-base font-normal text-primary-light-1! transition-all duration-200 ease-in-out',
					props.labelClass
				),
				{ 'text-error!': error },
				{
					'text-base! font-normal! text-black/40! cursor-not-allowed':
						props.disabled
				}
			]"
			@click="!props.disabled && inputRef?.focus()"
		>
			{{ props.placeholder }}
		</label>
	</div>
</template>
